<template>
  <div>
    <Navbar />
    <div class="container">
      <LoadingWrapper :loading-bool="loading">
        <Errors :errors="errors" />
        <div v-if="!hasError">
          <div v-if="site != null" class="row mt-5 mb-3">
            <div class="col-12">
              <h1>Results</h1>
              <h4>{{ site.name }}</h4>
              <p>{{ site.url }}</p>
            </div>
          </div>

          <DropdownSelector :site-id="siteId" :evaluation-id="evaluationId" />

          <b-card class="mt-3 mb-3 shadow">
            <h3 class="card-title">
              Overall Score
            </h3>
            <div class="row">
              <div class="col-md-5 text-center pt-4 pb-4">
                <ProgressCircle :percentage="score" />
              </div>
              <div class="col-md-7 pl-5 pr-5 pt-2" v-html="scoreDescription" />
            </div>
          </b-card>


          <b-card v-for="(stage, index) in perStageData" :key="index" class="stage mt-3 mb-3 shadow">
            <h5 class="card-title">
              {{ stage.name }}
            </h5>
            <div class="row">
              <div class="col-md-9 pt-4 pb-1">
                <div v-if="stage[2].length" class="mb-4">
                  <p><strong>Good areas</strong></p>
                  <div v-for="(question, id) in stage[2]" :key="`0${id}`">
                    <p>
                      <i v-html="checkSVG" /> {{ question.question }}
                    </p>
                    <blockquote v-if="question.note" class="sub-note">
                      <i v-html="bookSVG" /> {{ question.note }}
                    </blockquote>
                    <a v-for="imageNote in imageNotes[question.id]" :key="imageNote.id" class="sub-note"
                       target="_blank"
                       :href="imageNote.url">
                      <i v-html="fileMediaSVG" /> {{ renderDateAndTime(imageNote.created_at) }}
                    </a>
                  </div>
                </div>

                <div v-if="stage[1].length || stage[0].length" class="mb-4">
                  <p><strong>Areas to improve</strong></p>
                  <div v-for="(question, id) in stage[1]" :key="`1${id}`">
                    <p>
                      <i v-html="alertSVG" /> {{ question.question }}
                    </p>
                    <blockquote v-if="question.note" class="sub-note">
                      <i v-html="bookSVG" /> {{ question.note }}
                    </blockquote>
                    <a v-for="imageNote in imageNotes[question.id]" :key="imageNote.id" class="sub-note"
                       target="_blank"
                       :href="imageNote.url">
                      <i v-html="fileMediaSVG" /> {{ renderDateAndTime(imageNote.created_at) }}
                    </a>
                  </div>
                  <div v-for="(question, id) in stage[0]" :key="`2${id}`">
                    <p>
                      <i v-html="xSVG" /> {{ question.question }}
                    </p>
                    <blockquote v-if="question.note" class="sub-note">
                      <i v-html="bookSVG" /> {{ question.note }}
                    </blockquote>
                    <a v-for="imageNote in imageNotes[question.id]" :key="imageNote.id" class="sub-note"
                       target="_blank"
                       :href="imageNote.url">
                      <i v-html="fileMediaSVG" /> {{ renderDateAndTime(imageNote.created_at) }}
                    </a>
                  </div>
                </div>

                <div v-if="stage[null].length" class="mb-4">
                  <p><strong>Not Applicable</strong></p>
                  <div v-for="(question, id) in stage[null]" :key="`null${id}`">
                    <p>
                      <i v-html="circleSlashSVG" /> {{ question.question }}
                    </p>
                    <blockquote v-if="question.note" class="sub-note">
                      <i v-html="bookSVG" /> {{ question.note }}
                    </blockquote>
                    <a v-for="imageNote in imageNotes[question.id]" :key="imageNote.id" class="sub-note"
                       target="_blank"
                       :href="imageNote.url">
                      <i v-html="fileMediaSVG" /> {{ renderDateAndTime(imageNote.created_at) }}
                    </a>
                  </div>
                </div>
              </div>
              <div v-if="stage.score != null" class="col-md-3 pt-4 pb-4 text-center">
                <ProgressCircle :percentage="stage.score" />
              </div>
            </div>
          </b-card>
        </div>
      </LoadingWrapper>
    </div>
    <Footer />
  </div>
</template>

<style lang="scss" scoped>
.stage {
  ::v-deep h5 {
    text-transform: uppercase;
  }

  .sub-note {
    margin: 0 0 0 1.5em;
    display: block;
  }

  p {
    margin: .5em 0 0;

    i {
      display: inline-block;
      width: 1em;
      position: relative;
      top: -1px;

      ::v-deep .octicon-check {
        fill: #66b366;
      }

      ::v-deep .octicon-stop {
        fill: #FFCB38;
      }

      ::v-deep .octicon-circle-slash {
        fill: #808080;
      }

      ::v-deep .octicon-x {
        fill: #ff583b;
      }
    }
  }
}
</style>


<script>
import apiMixin from "../../utils/api"
import LoadingWrapper from "../navigation/LoadingWrapper"
import DropdownSelector from "./subcomponents/DropdownSelector"
import ProgressCircle from "./subcomponents/ProgressCircle"
import Footer from "../navigation/Footer"
import Navbar from "../navigation/Navbar"
import octicons from "@primer/octicons"
import resultsSummary from '../../resources/results_summary.json'
import datesMixin from "../../utils/dates"
import Errors from "../errors"


export default {
  name: 'EvaluationDetail',
  components: {Errors, Footer, LoadingWrapper, Navbar, DropdownSelector, ProgressCircle},
  mixins: [apiMixin, datesMixin],
  props: {
    siteId: {type: Number, required: true},
    evaluationId: {type: Number, required: true},
  },
  data() {
    return {
      site: null,
      url: null,

      score: null,
      response_format: [],
      evaluationData: {},
      currentlyShowingQuestion: null,

      bookSVG: octicons.book.toSVG(),
      checkSVG: octicons.check.toSVG(),
      alertSVG: octicons.stop.toSVG(),
      circleSlashSVG: octicons["circle-slash"].toSVG(),
      xSVG: octicons["x"].toSVG(),
      fileMediaSVG: octicons['file-media'].toSVG({height: 16}),

      resultsSummary,
      imageNotes: {}
    }
  },
  computed: {
    scoreDescription() {
      if (this.score == null) {
        return null
      }
      if (this.score >= 75) {
        return greatScoreDescription
      }
      if (this.score >= 40) {
        return goodScoreDescription
      }
      return poorScoreDescription
    },
    perStageData() {
      let stageData,
          evaluationData = this.evaluationData
      return this.response_format.reduce((arr, stage, i) => {
        let nonNullKeys = Object.keys(stage.data)
            .filter(key => evaluationData[key] != null)
        stageData = {
          name: `0${i + 1} ${stage.name}`,
          score: this.calculateScore(nonNullKeys),
          2: [],
          1: [],
          0: [],
          null: []
        }

        Object.keys(stage.data).forEach(key => {
          stageData[evaluationData[key]].push({
            question: resultsSummary[key],
            note: this.notes[key],
            id: key
          })
        })

        if (stageData[2].length || stageData[1].length || stageData[0].length || stageData[null].length) {
          arr.push(stageData)
        }
        return arr
      }, [])

    },
  },
  methods: {
    calculateScore(scoreKeys) {
      if (!scoreKeys.length) {
        return null
      }
      let total = scoreKeys.reduce((prev, key) => prev + this.evaluationData[key], 0)
      return Math.floor((total / (scoreKeys.length * 2)) * 100)
    },

    async load() {
      await this.withLoading(async () => {
        try {
          let evaluationResponse = await this.get(
              `/api/sites/${this.siteId}/evaluations/${this.evaluationId}/`
          )
          this.site = evaluationResponse.site
          this.score = evaluationResponse.score
          this.evaluationData = evaluationResponse.evaluation_data
          this.response_format = evaluationResponse.response_format
          this.notes = evaluationResponse.notes.reduce((accumulator, currVal) => {
            accumulator[currVal.question_id] = currVal.content
            return accumulator
          }, {})

          const imageNotes = await this.get(
              `/api/sites/${this.siteId}/evaluations/${this.evaluationId}/image-notes/`
          )
          this.imageNotes = imageNotes.items.reduce((accumulator, item) => {
            if (accumulator[item.question_id]) {
              accumulator[item.question_id].push(item)
            } else {
              accumulator[item.question_id] = [item]
            }
            return accumulator
          }, {})
        } catch (e) {
          this.catchApiError(e, false)
        }
      })

    }

  }
}

const greatScoreDescription = `<p>Overall the website reviewed scored great in this heuristic evaluation.</p><p>This means that the nearly all of important UX elements were in place and working well. However, there is still room for improvement as any one of the areas reviewed could make a big impact on the performance of your business.</p><p>It is best to review the below information to understand the specific areas or themes that still require improvement. From this you can draw up a list of things to improve. Consider elements which are at the beginning of the journey as higher priority. Remember if you are regularly changing elements on your website you should consider re-assessing your website by doing another heuristic evaluation.</p>`,
    goodScoreDescription = `<p>Overall the website reviewed scored well in this heuristic evaluation.</p><p>This means that the majority of important UX elements were in place and working well. However, there is still room for improvement as any one of the areas reviewed could make a big impact on the performance of your business.</p><p>It is best to review the below information to understand the specific areas or themes that still require improvement. From this you can draw up a list of things to improve. Consider elements which are at the beginning of the journey as higher priority. You can then reassess your website by doing another heuristic evaluation once the improvements have been made.</p>`,
    poorScoreDescription = `<p>Overall the website reviewed scored poorly in this heuristic evaluation.</p><p>This means that a large number of elements require work to improve them for your users or customers. Without getting the fundamentals right it will be hard to improve your website further.</p><p>It is best to review the below information to understand if there are specific areas or themes which will help you draw up a list of improvements. Consider elements which are at the beginning of the journey as higher priority.You can then reassess your website by doing another heuristic evaluation once the improvements have been made.</p>`

</script>